<template>
	<SuiStatusPicker
		:items="filteredStatuses"
		:is-loading="loading"
		:is-disabled="isDisabled"
		:model-value="selectedStatus"
		@update:model-value="updateStatus"
		@click="loadStatuses"
	/>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';
import {
	SuiStatusPicker,
	type SuiStatusPickerItem,
} from '@developers/scoro-ui';
import { useMfeUtils, usePermissions, useTranslation } from '@/composables';
import { useMessage } from '@/features/Message';
import {
	StatusModulesEnum,
	type Status,
	useStatusChangeMutation,
	useStatusesListLazyQuery,
	StatusesListDocument,
	ProjectDetailsHeaderDocument,
	type StatusPickerStatusFragment,
} from '@/generated/graphql';
import { AmplitudeCustomEvents, track } from '@/lib/amplitude';
import { getModuleTypeByStatusModuleEnum } from '@/utils/misc';
import {
	STATUS_COMPLETED,
	STATUS_ERROR_PROJECT_ACTIVE_EVENTS,
} from './constants';
import { Permission, PermissionAction } from '@/constants/permissions';

export type StatusPickerProps = {
	module: StatusModulesEnum;
	objectId: string;
	status: StatusPickerStatusFragment;
	isDisabled?: boolean;
};
const props = defineProps<StatusPickerProps>();

const { t } = useTranslation();
const { addErrorMessage } = useMessage();
const { rootEmit } = useMfeUtils();
const { can } = usePermissions();

const { status, isDisabled } = toRefs(props);

const canChangeProjectStatusToCompleted = computed(() =>
	can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_CHANGE_PROJECT_STATUS_TO_COMPLETED
	)
);

const selectedStatus = computed(() => {
	return {
		id: status.value.statusId,
		name: status.value.name,
		color: status.value.color,
	};
});

const statusesListQueryVariables = computed(() => ({
	statusesListInput: {
		filters: {
			module: getModuleTypeByStatusModuleEnum(props.module),
			inUse: true,
		},
	},
}));

const {
	load,
	result: statusesResult,
	loading,
} = useStatusesListLazyQuery(statusesListQueryVariables.value, {
	notifyOnNetworkStatusChange: true,
	enabled: !isDisabled.value,
});

const { mutate } = useStatusChangeMutation({
	context: {
		handleErrorLocally: true,
	},
	errorPolicy: 'all',
});

const loadStatuses = () => {
	load(StatusesListDocument, statusesListQueryVariables.value);
};

const updateStatus = async (status: SuiStatusPickerItem) => {
	const result = await mutate(
		{
			input: {
				module: props.module,
				objectId: props.objectId.toString(),
				statusId: status.id,
			},
		},
		{
			refetchQueries:
				props.module === StatusModulesEnum.Projects
					? [
							{
								query: ProjectDetailsHeaderDocument,
								variables: {
									projectInput: {
										projectId: Number(props.objectId),
									},
								},
							},
						]
					: [],
		}
	);

	if (
		result?.errors &&
		result.errors.find(
			(error) => error.message === STATUS_ERROR_PROJECT_ACTIVE_EVENTS
		)
	) {
		rootEmit('project-status-completed', {
			projectId: String(props.objectId),
		});

		return;
	} else if (!result?.data?.StatusChange) {
		addErrorMessage({
			message: t('status_picker_saving_fail'),
		});
	} else if (status.id === STATUS_COMPLETED) {
		if (props.module === StatusModulesEnum.Projects) {
			track(AmplitudeCustomEvents.ProjectCompleted);
		} else if (props.module === StatusModulesEnum.Quotes) {
			track(AmplitudeCustomEvents.QuoteCompleted);
		}
	}
};

const isStatusSelectable = (statusId: Status['statusId']) =>
	!(
		props.module === StatusModulesEnum.Projects &&
		!canChangeProjectStatusToCompleted.value &&
		statusId === STATUS_COMPLETED
	);

const filteredStatuses = computed<SuiStatusPickerItem[]>(
	() =>
		statusesResult.value?.StatusesList?.filter(
			(row) => row.name.trim() !== '' && isStatusSelectable(row.statusId)
		).map((row) => ({
			id: row.statusId,
			name: row.name,
			color: row.color,
		})) || []
);
</script>
